import {
	ClientSideDataGridModel,
	ClientSideDataGridModelRenderProps,
} from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import {
	CatalogPartRevisionBomDataGridApi,
	CatalogPartRevisionBomDataGridApi_DataGridData,
} from "src/api/generated/erp/parts/catalogPart/api/catalogPartRevisionBomDataGridApi.ts";
import { InlineCrudDataGrid, InlineCrudDataGridApi } from "src/components/common/dataGrid/crud/InlineCrudDataGrid.tsx";
import i18n from "i18next";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { nullableAavoObjectRef } from "src/utils/aavoObjectRefUtils.ts";
import { AavoButton } from "src/components/common/buttons/AavoButton.tsx";
import { faSitemap } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { CatalogPartRevisionBomView } from "src/components/views/erp/parts/catalogPart/CatalogPartRevisionBomView.tsx";
import React from "react";
import { AavoDataGridApi } from "src/components/common/dataGrid/AavoDataGridApi.ts";
import { AavoTextField } from "src/components/common/inputFields/AavoTextField.tsx";
import { MenuCheckbox } from "src/components/common/contextMenu/MenuCheckbox.tsx";
import { DataDirtyStateChangeHandler } from "src/utils/dataDirtyStateChangeHandler.ts";
import { CatalogPartRevisionView } from "src/api/generated/erp/db/types/tables/catalogPartRevisionView";
import {
	CatalogPartRevisionBomDataGridRow,
	getCatalogPartRevisionBomDataGridColumns,
	getDefaultRefNo,
} from "src/components/views/erp/parts/catalogPart/CatalogPartRevisionBomDataGrid.utils.tsx";
import { OpenCatalogPartButton } from "src/components/views/erp/utilComponents/OpenCatalogPartButton.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CatalogPartRevisionBomDataGridProps {
	catalogPartRevision: CatalogPartRevisionView;
	onRowSelectionChanged?: (selectedRows: CatalogPartRevisionBomDataGridRow[]) => void;
	inlineCrudGridApiRef?: React.RefObject<InlineCrudDataGridApi | null>;
	gridApiRef?: React.RefObject<AavoDataGridApi<CatalogPartRevisionBomDataGridRow> | null>;
	dataDirtyStateChanged?: DataDirtyStateChangeHandler;
	readOnly?: boolean;
}

export const CatalogPartRevisionBomDataGrid = ({
	catalogPartRevision,
	onRowSelectionChanged,
	inlineCrudGridApiRef,
	gridApiRef,
	dataDirtyStateChanged,
	readOnly,
}: CatalogPartRevisionBomDataGridProps) => {
	return (
		<ClientSideDataGridModel
			customFunctionUiActionView={"CATALOG_PART_REVISION_LINES"}
			apiRef={gridApiRef}
			gridId={"98251C8B5141695F"}
			fetchData={(params) =>
				CatalogPartRevisionBomDataGridApi.getData({
					catalogPartRevisionId: catalogPartRevision.catalogPartRevisionId,
					...params,
				})
			}
			initialParams={{
				searchQuery: "",
				showInDesignUnit: false,
			}}
			getRows={(data) => data.revisionLines}
			getRowId={(row) => row.catalogPartRevisionLineId}
			onSelectionChanged={onRowSelectionChanged}
			render={(params) => (
				<DataGrid
					{...params}
					catalogPartRevision={catalogPartRevision}
					inlineCrudGridApiRef={inlineCrudGridApiRef}
					dataDirtyStateChanged={dataDirtyStateChanged}
					readOnly={readOnly}
				/>
			)}
		/>
	);
};

interface DataGridProps extends ClientSideDataGridModelRenderProps<
	CatalogPartRevisionBomDataGridApi_DataGridData,
	CatalogPartRevisionBomDataGridRow,
	{ searchQuery: string; showInDesignUnit: boolean }
> {
	catalogPartRevision: CatalogPartRevisionView;
	inlineCrudGridApiRef?: React.RefObject<InlineCrudDataGridApi | null>;
	dataDirtyStateChanged?: DataDirtyStateChangeHandler;
	readOnly?: boolean;
}

const DataGrid = ({
	data: { bomLineGroupOptions },
	onlySelectedRow,
	currentParams: { showInDesignUnit },
	dataGridProps,
	refreshData,
	getApi,
	catalogPartRevision,
	inlineCrudGridApiRef,
	dataDirtyStateChanged,
	readOnly,
}: DataGridProps) => {
	const { openDialog } = useGenericDialog();
	const editable =
		!readOnly && (catalogPartRevision.state === "INITIAL" || catalogPartRevision.unrestrictedEditingCascaded);

	function getQuantityInBaseUnit(revisionLine: CatalogPartRevisionBomDataGridRow) {
		if (showInDesignUnit) return (revisionLine.designUnitQuantity ?? 0) * revisionLine.linePartDesignUnitFactor;
		else return revisionLine.quantity;
	}

	return (
		<InlineCrudDataGrid<CatalogPartRevisionBomDataGridRow>
			inlineCrudGridApiRef={inlineCrudGridApiRef}
			disableMultipleRowSelection
			dataDirtyStateChanged={dataDirtyStateChanged}
			columns={getCatalogPartRevisionBomDataGridColumns({
				showInDesignUnit,
				bomLineGroupOptions,
			})}
			actionBarComponents={
				<>
					<DocumentsOfObjectButton
						objectRef={nullableAavoObjectRef(
							"CATALOG_PART_REVISION",
							onlySelectedRow?.linePartLatestRevisionId,
						)}
					/>
					<AavoButton
						icon={faSitemap}
						label={i18n.t("product_structure")}
						disabled={onlySelectedRow == null || onlySelectedRow.linePartLatestRevisionId == null}
						variant={"outlined"}
						onClick={() => {
							if (onlySelectedRow == null || onlySelectedRow.linePartLatestRevisionId == null) return;
							openDialog({
								title: `${i18n.t("product_structure")} 
											(${onlySelectedRow.linePartNo} 
											${onlySelectedRow.linePartDescription_1} 
											${onlySelectedRow.linePartLatestRevisionNumber})`,

								content: (
									<CatalogPartRevisionBomView
										catalogPartRevisionId={onlySelectedRow.linePartLatestRevisionId}
										readOnly={readOnly}
									/>
								),
								agentContext: {
									key: defineAgentUiContextKey(
										"erp.parts.catalogPartRevisionLineProductStructure.dialog",
									),
									title: i18n.t("product_structure"),
									pathSegments: [`${i18n.t("part")} ${onlySelectedRow.linePartNo}`],
									data: {
										catalogPartRevisionId: onlySelectedRow.linePartLatestRevisionId,
										catalogPartRevisionLineId: onlySelectedRow.catalogPartRevisionLineId,
										lineCatalogPartId: onlySelectedRow.lineCatalogPartId,
									},
								},
							});
						}}
					/>
					<AavoTextField
						label={i18n.t("search")}
						onSubmit={async (value) => {
							await refreshData({ searchQuery: value });
						}}
					/>
				</>
			}
			actionBarMenuComponents={[
				<MenuCheckbox
					key={"showInDesignUnit"}
					label={i18n.t("show_in_design_unit")}
					defaultChecked={showInDesignUnit}
					onChange={async (value) => {
						await refreshData({ showInDesignUnit: value });
					}}
				/>,
			]}
			rowContextMenuComponents={({ row }) => [
				<OpenCatalogPartButton key={"openCatalogPart"} catalogPartId={row.lineCatalogPartId} />,
			]}
			add={
				editable &&
				(() => ({
					referenceNo: getDefaultRefNo(getApi()),
					catalogPartRevisionId: catalogPartRevision.catalogPartRevisionId,
					quantity: 1,
					acquisitionMethod: "WAREHOUSE",
					isPhantom: false,
					note: "",
					configuratorTransformationScript: "",
					bomLineGroupId: null,
				}))
			}
			save={
				editable &&
				(({ items }) =>
					CatalogPartRevisionBomDataGridApi.save({
						revisionLines: items.map((item) => ({
							...item,
							quantity: getQuantityInBaseUnit(item),
						})),
					}))
			}
			remove={
				editable &&
				(async ({ items }) => {
					await CatalogPartRevisionBomDataGridApi.delete_({
						revisionLines: items,
					});
				})
			}
			agentContext={{
				key: defineAgentUiContextKey("erp.parts.catalogPartRevisionBom.grid"),
				data: {
					catalogPartId: catalogPartRevision.catalogPartId,
					catalogPartRevisionId: catalogPartRevision.catalogPartRevisionId,
				},
				rowFields: ["catalogPartRevisionLineId", "catalogPartRevisionId", "lineCatalogPartId", "referenceNo"],
				selectedRowPathSegment: (row) => `${i18n.t("reference_no")} ${row.referenceNo}`,
			}}
			{...dataGridProps}
		/>
	);
};
